import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import * as global from "../data/global";
import { saveSelectedSite } from "../data/global";
import { MuiMaterials } from "../@index.js";
import { createApiEndpoint } from "../index.js";
import { isHumiditySensor } from "./siteSetupDefaults.js";
import ModuleViewerDialog from "./ModuleViewerDialog.js";
import { useModuleOutputToggles } from "./useModuleOutputToggles.js";
import {
    createWindowChromeTheme,
    getWindowChromeThemeColor,
    ModalBody,
    ModalContentTile,
    SiteSelectPhase,
    SiteWindowHeaderLeading,
    SiteWindowToolNav,
    ChromeHeaderBar,
    Spinner,
    SITE_WINDOW_IDS,
    outlinedActionButtonSx,
    useChromeHeaderLayout,
    CHROME_ACTIONS_FILL_CLASS,
} from "./windowChrome/index.js";

const getThemeColor = getWindowChromeThemeColor;
const SENSOR_COLUMNS = 21;
const FAST_SECONDS = 0;
const SECOND_OPTIONS = [
    { value: FAST_SECONDS, label: "Fast" },
    { value: 3, label: "3" },
    { value: 5, label: "5" },
    { value: 10, label: "10" },
    { value: 30, label: "30" },
    { value: 60, label: "60" },
    { value: 300, label: "300" },
];

function parseJsonPayload(raw) {
    let parsed = raw;
    for (let depth = 0; depth < 3 && typeof parsed === "string"; depth += 1) {
        try {
            parsed = JSON.parse(parsed);
        } catch {
            return null;
        }
    }
    return parsed && typeof parsed === "object" ? parsed : null;
}

function cablesFromLoadedSite() {
    const bins = global.gw.selectedSite?.data?.bins || [];
    const cables = [];
    for (let b = 0; b < bins.length; b++) {
        const bin = bins[b];
        const binName = bin.name || bin.binName || "";
        const list = bin.cableList || [];
        for (let c = 0; c < list.length; c++) {
            const cable = list[c];
            const id = Number(cable.id);
            if (!Number.isFinite(id)) continue;
            const sensors = (cable.sensorList || []).filter((s) => !isHumiditySensor(s));
            cables.push({
                id,
                name: cable.name || String(id),
                binName,
                sectionName: cable.sectionName || "",
                sensorCount: sensors.length || Number(cable.sensorCount) || 0,
            });
        }
    }
    return cables;
}

function cableLabel(cable) {
    if (!cable) return "";
    const parts = [cable.binName, cable.name ? `Cable ${cable.name}` : ""]
        .map((part) => (part || "").trim())
        .filter(Boolean);
    return parts.join(" ");
}

function measureLabelWidth(text) {
    const value = text || "";
    if (typeof document === "undefined") return value.length * 8;
    const canvas = measureLabelWidth.canvas || (measureLabelWidth.canvas = document.createElement("canvas"));
    const context = canvas.getContext("2d");
    if (!context) return value.length * 8;
    context.font = "13px Roboto, Helvetica, Arial, sans-serif";
    return context.measureText(value).width;
}

function formatReading(value, mode) {
    const text = value == null ? "" : String(value).trim();
    if (!text) return "";
    if (text === "-O-" || text === "-E-" || text === "-e-") return text.toUpperCase() === "-E-" ? "-E-" : "-O-";
    if (mode === "resistance") return text.endsWith("Ω") ? text : `${text}Ω`;
    return text.endsWith("°F") ? text : `${text}°F`;
}

function clampByte(value) {
    return Math.max(0, Math.min(255, Math.round(value)));
}

function parseTemperature(value) {
    const text = value == null ? "" : String(value).trim().replace("°F", "").replace("Ω", "");
    if (!text || text.startsWith("-")) return null;
    const temperature = Number(text);
    return Number.isFinite(temperature) ? temperature : null;
}

function readingColor(value, mode, previousValue, neutral) {
    const text = value == null ? "" : String(value).trim();
    if (!text || text.startsWith("-")) return "#c0c0c0";
    if (mode === "resistance") return neutral;
    const temperature = parseTemperature(text);
    const lastTemp = parseTemperature(previousValue);
    if (temperature == null || lastTemp == null) return neutral;
    const delta = lastTemp - temperature;
    if (delta <= -2) return `rgb(255, ${clampByte(200 - delta)}, 0)`;
    if (delta >= 2) return `rgb(0, ${clampByte(200 + delta)}, 255)`;
    return neutral;
}

const DIAGNOSTIC_NUMBER = "#f3f3f3";

function DiagnosticsHeaderActions({
    fieldSx,
    cableSelectWidth,
    cables,
    cableId,
    handleCableChange,
    seconds,
    handleSecondsChange,
    mode,
    handleModeChange,
    moduleViewerOpen,
    closeModuleViewer,
    openModuleViewer,
    busy,
    running,
    scanConflict,
    stopTest,
    startTest,
}) {
    const { placeInBar: actionsInBar } = useChromeHeaderLayout();

    return (
        <MuiMaterials.Box
            className={actionsInBar ? CHROME_ACTIONS_FILL_CLASS : undefined}
            sx={
                actionsInBar
                    ? {
                        width: "100%",
                        minWidth: 0,
                        display: "flex",
                        flexWrap: "wrap",
                        alignItems: "center",
                        justifyContent: "flex-start",
                        columnGap: 1.5,
                        rowGap: 1,
                    }
                    : {
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                        flexShrink: 1,
                        minWidth: 0,
                    }
            }
        >
            <MuiMaterials.FormControl
                size="small"
                sx={{
                    ...fieldSx,
                    "&&.MuiFormControl-root": actionsInBar
                        ? {
                            width: `${cableSelectWidth}px !important`,
                            minWidth: `${cableSelectWidth}px !important`,
                            maxWidth: "none !important",
                            flexShrink: 0,
                        }
                        : {
                            width: `${Math.min(cableSelectWidth, 180)}px !important`,
                            minWidth: `${Math.min(cableSelectWidth, 156)}px !important`,
                            maxWidth: `${Math.min(cableSelectWidth, 220)}px !important`,
                            flexShrink: 1,
                        },
                    "& .MuiSelect-select": {
                        padding: "4px 32px 4px 14px",
                        height: "32px",
                        minHeight: "0",
                        lineHeight: "22px",
                        boxSizing: "border-box",
                        display: "flex",
                        alignItems: "center",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                    },
                }}
            >
                <MuiMaterials.InputLabel id="diag-cable-label">Cable</MuiMaterials.InputLabel>
                <MuiMaterials.Select
                    labelId="diag-cable-label"
                    label="Cable"
                    value={cables.some((cable) => String(cable.id) === String(cableId)) ? String(cableId) : ""}
                    onChange={(event) => handleCableChange(event.target.value)}
                    MenuProps={{
                        PaperProps: {
                            sx: {
                                width: "max-content",
                                minWidth: cableSelectWidth,
                                maxWidth: "none",
                            },
                        },
                    }}
                >
                    {cables.map((cable) => (
                        <MuiMaterials.MenuItem
                            key={`cable-${cable.id}`}
                            value={String(cable.id)}
                            sx={{ whiteSpace: "nowrap", px: 1.75 }}
                        >
                            {cableLabel(cable)}
                        </MuiMaterials.MenuItem>
                    ))}
                </MuiMaterials.Select>
            </MuiMaterials.FormControl>
            <MuiMaterials.FormControl
                size="small"
                sx={{
                    ...fieldSx,
                    minWidth: actionsInBar ? 110 : 0,
                    ...(!actionsInBar
                        ? {
                            "&&.MuiFormControl-root": {
                                width: "104px !important",
                                minWidth: "104px !important",
                                maxWidth: "120px !important",
                                flexShrink: 0,
                            },
                        }
                        : {}),
                }}
            >
                <MuiMaterials.InputLabel id="diag-seconds-label">Seconds</MuiMaterials.InputLabel>
                <MuiMaterials.Select
                    labelId="diag-seconds-label"
                    label="Seconds"
                    value={seconds}
                    onChange={(event) => handleSecondsChange(event.target.value)}
                >
                    {SECOND_OPTIONS.map((option) => (
                        <MuiMaterials.MenuItem key={`seconds-${option.value}`} value={option.value}>
                            {option.label}
                        </MuiMaterials.MenuItem>
                    ))}
                </MuiMaterials.Select>
            </MuiMaterials.FormControl>
            <MuiMaterials.RadioGroup
                row={!actionsInBar}
                value={mode}
                onChange={(event) => handleModeChange(event.target.value)}
                sx={{
                    display: actionsInBar ? "flex" : "contents",
                    flexDirection: actionsInBar ? "row" : undefined,
                    flexWrap: actionsInBar ? "wrap" : undefined,
                    gap: actionsInBar ? 1 : undefined,
                    ...(actionsInBar ? { width: "auto", justifyContent: "flex-start" } : {}),
                    color: getThemeColor("#333333", "#f4cc8e"),
                }}
            >
                <MuiMaterials.FormControlLabel
                    value="temperature"
                    control={<MuiMaterials.Radio size="small" sx={{ color: getThemeColor("#666666", "#f4cc8e"), p: 0, mr: "6px" }} />}
                    label="Temperature"
                    sx={{ m: 0 }}
                />
                <MuiMaterials.FormControlLabel
                    value="resistance"
                    control={<MuiMaterials.Radio size="small" sx={{ color: getThemeColor("#666666", "#f4cc8e"), p: 0, mr: "6px" }} />}
                    label="Resistance"
                    sx={{ m: 0 }}
                />
            </MuiMaterials.RadioGroup>
            <MuiMaterials.Button
                size="small"
                variant="outlined"
                onClick={() => (moduleViewerOpen ? closeModuleViewer() : openModuleViewer())}
                sx={{
                    whiteSpace: "nowrap",
                    flexShrink: 0,
                    height: 32,
                    boxSizing: "border-box",
                    ...(actionsInBar ? { width: "auto" } : {}),
                    ...outlinedActionButtonSx(),
                }}
            >
                View Modules
            </MuiMaterials.Button>
            <MuiMaterials.Button
                size="small"
                variant="outlined"
                disabled={busy || (!running && (!cableId || scanConflict?.active))}
                onClick={() => (running ? stopTest() : startTest())}
                sx={{
                    whiteSpace: "nowrap",
                    flexShrink: 0,
                    height: 32,
                    boxSizing: "border-box",
                    ...(actionsInBar ? { width: "auto" } : { width: "auto", minWidth: 0 }),
                    ...outlinedActionButtonSx(),
                }}
            >
                {running ? "Stop Test" : "Start Test"}
            </MuiMaterials.Button>
        </MuiMaterials.Box>
    );
}

const DiagnosticsWindow = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const autoLoadHandledRef = useRef(false);
    const runningRef = useRef(false);
    const stopOnLeaveRef = useRef(false);
    const startedAtRef = useRef(0);
    const readingScrollRef = useRef(null);
    const cableIdRef = useRef("");

    const [forceUpdate, setForceUpdate] = useState(0);
    const [phase, setPhase] = useState("select");
    const [sites, setSites] = useState([]);
    const [sitesLoading, setSitesLoading] = useState(true);
    const carriedSiteGuid =
        (location.state?.autoLoad && location.state?.siteGuid) || location.state?.siteGuid || "";
    const [pickerSiteGuid, setPickerSiteGuid] = useState(() => carriedSiteGuid || "");
    const [loadedSiteGuid, setLoadedSiteGuid] = useState("");
    const [loading, setLoading] = useState(() => Boolean(carriedSiteGuid && location.state?.autoLoad));

    const [cables, setCables] = useState([]);
    const [cableId, setCableId] = useState("");
    const [seconds, setSeconds] = useState(FAST_SECONDS);
    const [mode, setMode] = useState("temperature");
    const [running, setRunning] = useState(false);
    const [rows, setRows] = useState([]);
    const [status, setStatus] = useState("");
    const [busy, setBusy] = useState(false);
    const [moduleViewerOpen, setModuleViewerOpen] = useState(false);
    const [scanConflict, setScanConflict] = useState(null);
    const {
        modulePoints,
        pendingOutputToggles,
        toggleOutput: toggleModuleOutput,
        applyPolledSession,
        clearPendingOutputToggles,
    } = useModuleOutputToggles({ lockedRef: runningRef, onError: setStatus });

    const { theme, textPrimary, textSecondary, paper, border, pageBg } = useMemo(
        () => createWindowChromeTheme(),
        [forceUpdate],
    );

    const fieldSx = useMemo(() => ({
        minWidth: 140,
        "& .MuiOutlinedInput-root": {
            height: 32,
            color: getThemeColor("#333333", "#f4cc8e"),
            backgroundColor: getThemeColor("#ffffff", "#2d2d2d"),
            fontSize: "13px",
            "& fieldset": { borderColor: getThemeColor("#cccccc", "rgba(244,204,142,0.5)") },
            "&.Mui-focused fieldset": {
                borderColor: getThemeColor("#cccccc", "rgba(244,204,142,0.5)"),
                borderWidth: 1,
            },
            "&:hover fieldset": {
                borderColor: getThemeColor("#cccccc", "rgba(244,204,142,0.5)"),
            },
        },
        "& .MuiInputLabel-root": { color: getThemeColor("#666666", "#a8abb1"), fontSize: "13px" },
        "& .MuiSelect-select": { paddingTop: "4px", paddingBottom: "4px", minHeight: "0 !important" },
        "& .MuiSvgIcon-root": { color: getThemeColor("#666666", "#a8abb1") },
    }), [forceUpdate]);

    const cableSelectWidth = useMemo(() => {
        const longest = cables.reduce((max, cable) => {
            const label = cableLabel(cable);
            return label.length > max.length ? label : max;
        }, "Cable");
        return Math.ceil(measureLabelWidth(longest) + 14 + 40);
    }, [cables]);

    const isAdmin = global.isAdminUser(global.gw?.email);

    useEffect(() => {
        if (!isAdmin) navigate("/sites", { replace: true });
    }, [isAdmin, navigate]);

    useEffect(() => {
        const handleThemeChange = () => setForceUpdate((prev) => prev + 1);
        window.addEventListener("themeChanged", handleThemeChange);
        return () => window.removeEventListener("themeChanged", handleThemeChange);
    }, []);

    useEffect(() => {
        const applySiteInfos = () => {
            const all = [...(global.gw.siteInfos || [])].filter((s) => s.siteGuid && !s.isHms);
            all.sort((a, b) =>
                (a.siteName || "").toLowerCase().localeCompare((b.siteName || "").toLowerCase()),
            );
            setSites(all);
            setSitesLoading(false);
        };

        let cancelled = false;
        const loadSites = async () => {
            if ((global.gw.siteInfos || []).length > 0) {
                if (!cancelled) applySiteInfos();
                return;
            }
            if (!global.gw?.token && !global.gw?.bolts) {
                if (!cancelled) setSitesLoading(false);
                return;
            }
            try {
                const response = await createApiEndpoint().fetchUserSites("direct");
                const fetched = response?.data?.fetchUserAttribute?.sites;
                if (!cancelled && Array.isArray(fetched) && fetched.length > 0 && (global.gw?.siteInfos?.length ?? 0) === 0) {
                    global.gw.siteInfos = fetched
                        .map((site, siteIndex) => ({
                            siteIndex,
                            siteGuid: site.siteGuid,
                            siteName: site.main?.siteName ?? "Site",
                            lastScan: site.lastScan,
                            isHms: site.isHms,
                            bins: site.bins ?? [],
                        }))
                        .filter((s) => s.siteGuid && !s.isHms);
                }
            } catch (err) {
                if (!cancelled) console.warn("DiagnosticsWindow: fetchUserSites", err);
            } finally {
                if (!cancelled) applySiteInfos();
            }
        };

        loadSites();
        return () => {
            cancelled = true;
        };
    }, [forceUpdate]);

    useLayoutEffect(() => {
        if (!location.state?.autoLoad || !location.state?.siteGuid) return;
        setPickerSiteGuid(location.state.siteGuid);
    }, [location.state?.autoLoad, location.state?.siteGuid]);

    const loadCatalog = useCallback(async () => {
        let nextCables = [];
        try {
            const response = await createApiEndpoint().fetchDiagnosticCables("direct");
            const payload = parseJsonPayload(response?.data?.getDiagnosticCables);
            if (Array.isArray(payload?.cables)) nextCables = payload.cables;
        } catch (err) {
            console.warn("DiagnosticsWindow: cables", err);
        }
        if (nextCables.length === 0) nextCables = cablesFromLoadedSite();
        setCables(nextCables);
        setCableId((current) => {
            const next = current && nextCables.some((cable) => String(cable.id) === String(current))
                ? String(current)
                : (nextCables[0] ? String(nextCables[0].id) : "");
            cableIdRef.current = next;
            return next;
        });
    }, []);

    const applySiteSelection = useCallback(async (siteGuid) => {
        if (!siteGuid) return;
        setLoading(true);
        try {
            const want = global.normalizeGuidForCompare
                ? global.normalizeGuidForCompare(siteGuid)
                : String(siteGuid || "").toLowerCase();
            const info = (global.gw.siteInfos || []).find((s) => {
                const g = s?.siteGuid;
                if (!g) return false;
                const have = global.normalizeGuidForCompare
                    ? global.normalizeGuidForCompare(g)
                    : String(g).toLowerCase();
                return have === want;
            });

            if (info) {
                if (global.gw.selectedSite?.refreshId) {
                    clearTimeout(global.gw.selectedSite.refreshId);
                }
                if (
                    !global.gw.selectedSite
                    || global.normalizeGuidForCompare?.(global.gw.selectedSite.siteGuid)
                        !== global.normalizeGuidForCompare?.(info.siteGuid)
                ) {
                    global.gw.selectedSite = new global.Site();
                }
                global.gw.selectedSite.siteIndex = info.siteIndex;
                global.gw.selectedSite.siteGuid = info.siteGuid;
                global.gw.selectedSite.siteName = info.siteName;
                global.gw.selectedSite.lastScan = info.lastScan;
                saveSelectedSite?.();
            } else if (global.gw.selectedSite) {
                global.gw.selectedSite.siteGuid = siteGuid;
            }

            await global.initializeCustomerSite(navigate);
            const loadedName =
                global.gw.selectedSite?.siteName
                || global.gw.selectedSite?.main?.siteName
                || global.gw.selectedSite?.data?.main?.siteName;
            if (loadedName) global.gw.selectedSite.siteName = loadedName;

            setLoadedSiteGuid(info?.siteGuid || siteGuid);
            setPickerSiteGuid(info?.siteGuid || siteGuid);
            setRows([]);
            setStatus("");
            setRunning(false);
            runningRef.current = false;
            setPhase("view");
            await loadCatalog();
        } catch (e) {
            console.error("DiagnosticsWindow: failed to load site", e);
            setPhase("select");
            setLoadedSiteGuid("");
        } finally {
            setLoading(false);
        }
    }, [loadCatalog, navigate]);

    useEffect(() => {
        const siteGuid = location.state?.siteGuid;
        if (!siteGuid || !location.state?.autoLoad) return;
        if (autoLoadHandledRef.current === siteGuid) return;
        autoLoadHandledRef.current = siteGuid;
        setPickerSiteGuid(siteGuid);
        applySiteSelection(siteGuid);
    }, [location.state, applySiteSelection]);

    const stopTest = useCallback(async () => {
        runningRef.current = false;
        setRunning(false);
        setStatus("");
        if (!global.gw?.email || !global.gw.selectedSite?.siteGuid) return;
        try {
            await createApiEndpoint().fetchAddDiagnostic("direct", JSON.stringify({ action: "stop" }));
        } catch (err) {
            console.warn("DiagnosticsWindow: stop", err);
        }
    }, []);

    useEffect(() => {
        stopOnLeaveRef.current = true;
        return () => {
            if (stopOnLeaveRef.current && runningRef.current) {
                createApiEndpoint().fetchAddDiagnostic("direct", JSON.stringify({ action: "stop" })).catch(() => {});
            }
        };
    }, [loadedSiteGuid]);

    useEffect(() => () => {
        createApiEndpoint().fetchAddDiagnostic("direct", JSON.stringify({ action: "hideModules" })).catch(() => {});
    }, []);

    const poll = useCallback(async () => {
        if (phase !== "view" || !global.gw.selectedSite?.siteGuid) return;
        try {
            const requestedAt = Date.now();
            const response = await createApiEndpoint().fetchDiagnostic("direct");
            const session = parseJsonPayload(response?.data?.getDiagnostic);
            if (!session) return;
            applyPolledSession(session, requestedAt);
            if (session.moduleViewerError) setStatus(String(session.moduleViewerError));
            if (session.scanConflict?.active) {
                setScanConflict(session.scanConflict);
            } else {
                setScanConflict(null);
            }
            if (!session.isRunning && runningRef.current && Date.now() - startedAtRef.current < 4000) {
                return;
            }
            if (!session.isRunning && !runningRef.current) {
                return;
            }
            const wantedCable = cableIdRef.current;
            if (wantedCable && session.cableId != null && String(session.cableId) !== String(wantedCable)) {
                return;
            }
            const sessionMode = session.mode === "resistance" ? "resistance" : "temperature";
            const readings = Array.isArray(session.readings) ? session.readings : [];
            setRows(readings
                .map((reading, index) => ({
                    key: `${reading.at || index}-${index}`,
                    at: reading.at || "",
                    cableName: reading.cableName || session.cableName || "",
                    values: Array.isArray(reading.values) ? reading.values.map((value) => String(value)) : [],
                    mode: sessionMode,
                }))
                .sort((a, b) => String(b.at).localeCompare(String(a.at))));
            setStatus(session.status || (session.isRunning ? "Reading…" : ""));
            if (session.isRunning) {
                runningRef.current = true;
                setRunning(true);
                if (session.cableId && !cableIdRef.current) {
                    cableIdRef.current = String(session.cableId);
                    setCableId(String(session.cableId));
                }
                if (session.seconds != null && session.seconds !== "") setSeconds(Number(session.seconds));
                if (session.mode) setMode(sessionMode);
            } else if (!runningRef.current) {
                setRunning(false);
            } else {
                runningRef.current = false;
                setRunning(false);
            }
        } catch (err) {
            console.warn("DiagnosticsWindow: poll", err);
        }
    }, [phase, applyPolledSession]);

    useEffect(() => {
        if (phase !== "view") return undefined;
        poll();
        const pollMs = moduleViewerOpen
            ? (pendingOutputToggles.length > 0 ? 50 : 100)
            : 250;
        const id = window.setInterval(poll, pollMs);
        return () => window.clearInterval(id);
    }, [phase, poll, loadedSiteGuid, moduleViewerOpen, pendingOutputToggles.length]);

    useEffect(() => {
        if (phase !== "view") return undefined;
        const beat = () => {
            createApiEndpoint().fetchAddDiagnostic("direct", JSON.stringify({ action: "heartbeat" })).catch(() => {});
        };
        beat();
        const id = window.setInterval(beat, 5000);
        const handlePageHide = () => {
            if (runningRef.current) {
                createApiEndpoint().fetchAddDiagnostic("direct", JSON.stringify({ action: "stop" })).catch(() => {});
            }
            createApiEndpoint().fetchAddDiagnostic("direct", JSON.stringify({ action: "hideModules" })).catch(() => {});
        };
        window.addEventListener("pagehide", handlePageHide);
        return () => {
            window.clearInterval(id);
            window.removeEventListener("pagehide", handlePageHide);
        };
    }, [phase, loadedSiteGuid]);

    const newestReadingKey = rows[0]?.key || "";
    useEffect(() => {
        if (readingScrollRef.current) readingScrollRef.current.scrollTop = 0;
    }, [newestReadingKey]);

    const cancelBlockingScan = async () => {
        if (!scanConflict?.scanId || !global.gw.selectedSite?.siteGuid) return;
        setBusy(true);
        try {
            const response = await createApiEndpoint().fetchUpdateScan("direct", JSON.stringify({
                id: scanConflict.scanId,
                dateFinished: new Date().toJSON(),
                isSuccessful: false,
                isComplete: true,
                isCanceled: true,
            }));
            const result = response?.data?.updateScan;
            if (result === "canceled" || result === "Saved." || result === "Updated.") {
                setScanConflict(null);
                setStatus("Site scan canceled. You can start diagnostics.");
                poll();
            } else {
                setStatus("Could not cancel the site scan.");
            }
        } catch (err) {
            console.warn("DiagnosticsWindow: cancel scan", err);
            setStatus("Could not cancel the site scan.");
        } finally {
            setBusy(false);
        }
    };

    const startTest = async (next = {}) => {
        const nextCableId = next.cableId ?? cableId;
        const nextMode = next.mode ?? mode;
        const nextSeconds = next.seconds ?? seconds;
        const cable = cables.find((item) => String(item.id) === String(nextCableId));
        if (!cable) {
            setStatus("Select a cable.");
            return;
        }
        setBusy(true);
        setRows([]);
        setStatus("Starting…");
        startedAtRef.current = Date.now();
        runningRef.current = true;
        setRunning(true);
        try {
            const response = await createApiEndpoint().fetchAddDiagnostic("direct", JSON.stringify({
                action: "start",
                cableId: Number(cable.id),
                cableName: cableLabel(cable),
                mode: nextMode,
                seconds: Number(nextSeconds),
            }));
            const session = parseJsonPayload(response?.data?.addDiagnostic);
            if (session?.error) {
                runningRef.current = false;
                setRunning(false);
                setStatus(session.error);
                if (session.scanConflict?.active) setScanConflict(session.scanConflict);
                return;
            }
            setScanConflict(null);
            setStatus("Waiting for the gateway…");
        } catch (err) {
            runningRef.current = false;
            setRunning(false);
            setStatus("Could not start diagnostics.");
            console.warn("DiagnosticsWindow: start", err);
        } finally {
            setBusy(false);
        }
    };

    const handleCableChange = (value) => {
        cableIdRef.current = String(value);
        setCableId(value);
        setRows([]);
        setStatus(runningRef.current ? "Reading…" : "");
        if (runningRef.current && value) startTest({ cableId: value });
    };

    const handleSecondsChange = (value) => {
        const next = Number(value);
        setSeconds(next);
        if (runningRef.current) startTest({ seconds: next });
    };

    const handleModeChange = (value) => {
        setMode(value);
        if (runningRef.current) startTest({ mode: value });
    };

    const closeModuleViewer = useCallback(() => {
        setModuleViewerOpen(false);
        clearPendingOutputToggles();
        createApiEndpoint().fetchAddDiagnostic("direct", JSON.stringify({ action: "hideModules" })).catch(() => {});
    }, [clearPendingOutputToggles]);

    const openModuleViewer = () => {
        setModuleViewerOpen(true);
        createApiEndpoint().fetchAddDiagnostic("direct", JSON.stringify({ action: "viewModules" })).catch((err) => {
            console.warn("DiagnosticsWindow: view modules", err);
        });
    };

    const handleClose = () => {
        stopOnLeaveRef.current = true;
        if (moduleViewerOpen) closeModuleViewer();
        if (runningRef.current) stopTest();
        navigate("/sites", { replace: true });
    };

    const handleContinue = (guid) => {
        const next = guid || pickerSiteGuid;
        if (!next) return;
        setPickerSiteGuid(next);
        applySiteSelection(next);
    };

    const handleHeaderSiteChange = (siteGuid) => {
        if (!siteGuid || siteGuid === loadedSiteGuid) return;
        if (runningRef.current) stopTest();
        setPickerSiteGuid(siteGuid);
        applySiteSelection(siteGuid);
    };

    const activeSiteGuid = loadedSiteGuid || pickerSiteGuid || "";

    if (!isAdmin) return null;

    return (
        <MuiMaterials.ThemeProvider theme={theme}>
            <div
                className="diagnosticsWindow"
                style={{
                    position: "fixed",
                    inset: 0,
                    zIndex: 1000,
                    display: "flex",
                    flexDirection: "column",
                    background: pageBg,
                    color: textPrimary,
                    paddingTop: "env(safe-area-inset-top, 0px)",
                    paddingBottom: "env(safe-area-inset-bottom, 0px)",
                }}
            >
                {loading ? (
                    <MuiMaterials.Box
                        sx={{
                            position: "absolute",
                            inset: 0,
                            zIndex: 2,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            backgroundColor: getThemeColor("rgba(232,232,232,0.7)", "rgba(26,26,26,0.7)"),
                        }}
                    >
                        <Spinner size="modal" />
                    </MuiMaterials.Box>
                ) : null}

                <ChromeHeaderBar
                    paper={paper}
                    border={border}
                    textSecondary={textSecondary}
                    onClose={handleClose}
                    leading={
                        phase === "view" ? (
                            <SiteWindowHeaderLeading
                                toolTitle="Diagnostics"
                                siteWindowId={SITE_WINDOW_IDS.diagnostics}
                                siteGuid={activeSiteGuid || null}
                                textSecondary={textSecondary}
                                border={border}
                                showSiteSelect
                                siteSelectValue={activeSiteGuid}
                                onSiteChange={handleHeaderSiteChange}
                                sites={sites}
                                filterSite={(s) => !s.isHms}
                                siteSelectDisabled={loading || sitesLoading}
                                includeTool={false}
                            />
                        ) : null
                    }
                    tool={
                        <SiteWindowToolNav
                            toolTitle="Diagnostics"
                            siteWindowId={SITE_WINDOW_IDS.diagnostics}
                            siteGuid={activeSiteGuid || null}
                            textSecondary={textSecondary}
                        />
                    }
                    actions={
                        phase === "view" && !loading ? (
                            <DiagnosticsHeaderActions
                                fieldSx={fieldSx}
                                cableSelectWidth={cableSelectWidth}
                                cables={cables}
                                cableId={cableId}
                                handleCableChange={handleCableChange}
                                seconds={seconds}
                                handleSecondsChange={handleSecondsChange}
                                mode={mode}
                                handleModeChange={handleModeChange}
                                moduleViewerOpen={moduleViewerOpen}
                                closeModuleViewer={closeModuleViewer}
                                openModuleViewer={openModuleViewer}
                                busy={busy}
                                running={running}
                                scanConflict={scanConflict}
                                stopTest={stopTest}
                                startTest={startTest}
                            />
                        ) : null
                    }
                />

                {phase === "select" ? (
                    <SiteSelectPhase
                        value={pickerSiteGuid}
                        onChange={setPickerSiteGuid}
                        onContinue={handleContinue}
                        onCancel={handleClose}
                        loading={sitesLoading || loading}
                        filterSite={(s) => !s.isHms}
                    />
                ) : null}

                {phase === "view" && !loading ? (
                    <ModalBody
                        className="hide-scrollbar"
                        sx={{
                            px: 0,
                            pt: 0,
                            pb: 0,
                        }}
                    >
                        {scanConflict?.active ? (
                            <MuiMaterials.Box
                                sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 1.5,
                                    flexWrap: "wrap",
                                    px: 1,
                                    py: 1.25,
                                    mb: 1.5,
                                    borderRadius: "8px",
                                    bgcolor: getThemeColor("#fff4e5", "#3d2f1a"),
                                    border: `1px solid ${getThemeColor("#e6a700", "#c9922e")}`,
                                    color: textPrimary,
                                    fontSize: 13,
                                }}
                            >
                                <MuiMaterials.Typography sx={{ flex: 1, fontSize: 13 }}>
                                    {scanConflict.message || "Another scan is running on this site."}
                                </MuiMaterials.Typography>
                                <MuiMaterials.Button
                                    size="small"
                                    variant="outlined"
                                    disabled={busy || running}
                                    onClick={cancelBlockingScan}
                                    sx={{ whiteSpace: "nowrap", ...outlinedActionButtonSx() }}
                                >
                                    Cancel site scan
                                </MuiMaterials.Button>
                            </MuiMaterials.Box>
                        ) : null}
                        <ModalContentTile
                            className="hide-scrollbar"
                            sx={{
                                overflow: "hidden",
                                marginBottom: 0,
                                mx: { xs: 0.5, sm: 1 },
                                my: { xs: 3, sm: 4 },
                                p: { xs: "12px 8px", sm: "16px 12px" },
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "stretch",
                                alignSelf: "stretch",
                                width: "auto",
                                boxSizing: "border-box",
                            }}
                        >
                            <MuiMaterials.TableContainer
                                ref={readingScrollRef}
                                className="hide-scrollbar"
                                sx={{
                                    width: "100%",
                                    flex: 1,
                                    minHeight: 0,
                                    overflowY: "auto",
                                    overflowX: "hidden",
                                    scrollbarWidth: "none",
                                    msOverflowStyle: "none",
                                    "&::-webkit-scrollbar": { display: "none", width: 0, height: 0 },
                                }}
                            >
                                <MuiMaterials.Table
                                    size="small"
                                    stickyHeader
                                    sx={{
                                        tableLayout: "fixed",
                                        width: "100%",
                                        "& .MuiTableCell-root": {
                                            padding: "4px 1px",
                                            fontSize: 12,
                                            lineHeight: 1.2,
                                        },
                                    }}
                                >
                                    <MuiMaterials.TableHead>
                                        <MuiMaterials.TableRow>
                                            <MuiMaterials.TableCell sx={{ color: textSecondary, fontWeight: 700, width: "8%" }}>Cable</MuiMaterials.TableCell>
                                            {Array.from({ length: SENSOR_COLUMNS }, (_, index) => (
                                                <MuiMaterials.TableCell
                                                    key={`h-${index}`}
                                                    align="center"
                                                    sx={{ color: textSecondary, fontWeight: 700, width: `${92 / SENSOR_COLUMNS}%` }}
                                                >
                                                    {`T${index + 1}`}
                                                </MuiMaterials.TableCell>
                                            ))}
                                        </MuiMaterials.TableRow>
                                    </MuiMaterials.TableHead>
                                    <MuiMaterials.TableBody>
                                        {rows.map((row, rowIndex) => (
                                            <MuiMaterials.TableRow key={row.key}>
                                                <MuiMaterials.TableCell
                                                    sx={{
                                                        color: textPrimary,
                                                        overflow: "hidden",
                                                        textOverflow: "ellipsis",
                                                        whiteSpace: "nowrap",
                                                    }}
                                                >
                                                    {row.cableName}
                                                </MuiMaterials.TableCell>
                                                {Array.from({ length: SENSOR_COLUMNS }, (_, index) => {
                                                    const raw = row.values[index] ?? "";
                                                    const shown = formatReading(raw, row.mode);
                                                    const previous = rows[rowIndex + 1]?.values?.[index];
                                                    return (
                                                        <MuiMaterials.TableCell
                                                            key={`${row.key}-${index}`}
                                                            align="center"
                                                            sx={{
                                                                color: readingColor(raw, row.mode, previous, getThemeColor("#333333", DIAGNOSTIC_NUMBER)),
                                                                fontVariantNumeric: "tabular-nums",
                                                                overflow: "hidden",
                                                                textOverflow: "ellipsis",
                                                                whiteSpace: "nowrap",
                                                            }}
                                                        >
                                                            {shown}
                                                        </MuiMaterials.TableCell>
                                                    );
                                                })}
                                            </MuiMaterials.TableRow>
                                        ))}
                                    </MuiMaterials.TableBody>
                                </MuiMaterials.Table>
                            </MuiMaterials.TableContainer>
                            <MuiMaterials.Typography
                                sx={{
                                    mt: 1.5,
                                    textAlign: "center",
                                    color: "#90ee90",
                                    fontSize: 13,
                                    minHeight: 20,
                                }}
                            >
                                {status}
                            </MuiMaterials.Typography>
                        </ModalContentTile>
                    </ModalBody>
                ) : null}
                <ModuleViewerDialog
                    open={moduleViewerOpen}
                    onClose={closeModuleViewer}
                    devices={global.gw.selectedSite?.devices || []}
                    modules={global.gw.selectedSite?.modules || []}
                    modulePoints={modulePoints}
                    outputsLocked={running}
                    pendingOutputToggles={pendingOutputToggles}
                    onToggleOutput={toggleModuleOutput}
                />
            </div>
        </MuiMaterials.ThemeProvider>
    );
};

export default DiagnosticsWindow;
